import React, { useContext } from 'react';

import { TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material';
import styled from 'styled-components';

import { type Order, type SummaryTableColumns } from './types';
import { AttendanceContext, View } from '../../context/AttendanceContext';

const HeadCell = styled(TableCell)`
    font-weight: bold;
    &:focus-within {
        border: 2px solid #ffba00;
    }
` as typeof TableCell;

interface HeadCellData {
    id: SummaryTableColumns;
    label: string;
}

export interface SummaryTableHeadProps {
    order: Order;
    orderBy: string;
    onRequestSort: (event: React.MouseEvent<unknown>, property: SummaryTableColumns) => void;
}

const SummaryTableHead: React.FC<SummaryTableHeadProps> = ({ order, orderBy, onRequestSort }) => {
    const { t, view, classInfo } = useContext(AttendanceContext);

    const headCells: HeadCellData[] = [
        { id: 'presences', label: t('presences') },
        { id: 'absences', label: t('absences') },
        { id: 'tardies', label: t('tardies') }
    ];

    if (view === View.StudentSummary) {
        headCells.unshift({ id: 'sortName', label: t('student') });
        if (classInfo.lms_sync_settings?.enable_attendance_grade) {
            headCells.push({ id: 'score', label: t('score') });
        }
    }

    if (view === View.SessionSummary) {
        headCells.unshift({ id: 'date', label: t('date') });
    }

    const createSortHandler = (property: SummaryTableColumns) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell, index) => (
                    <HeadCell
                        key={index}
                        align={index === 0 ? 'left' : 'center'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                        </TableSortLabel>
                    </HeadCell>
                ))}
                <HeadCell align="center">{t('report')}</HeadCell>
            </TableRow>
        </TableHead>
    );
};

export default SummaryTableHead;
